<template>
  <div class="view debug">
    <div>
      <details v-for="(group, gidx) in availableRequests" :key="`group${gidx}`">
        <summary>{{ group.label }}</summary>
        <ul>
          <li v-for="(request, ridx) in group.requests" :key="`group${gidx}-request${ridx}`" @click="showRequest(request)">
            <span class="type" :title="request.method | capitalize" :data-method="request.method" />
            <span class="label" v-html="request.label" />
          </li>
        </ul>
      </details>
    </div>
    <div>
      <div class="actions">
        <template v-if="activeRequest">
          <div class="method" :data-method="activeRequest.method">{{ activeRequest.method }}</div>
          <div class="url" v-if="activeRequest.url">{{ activeRequest.url }} </div>
          <div class="url" v-else-if="activeRequest.callback">{{ activeRequest.callback | callbackName }}</div>
          <Button primary small @click="executeRequest()">{{ activeRequest.method !== 'execute' ? 'Send' : 'Run' }}</Button>
        </template>
      </div>
      <div class="parameters">
        <div class="parameter" v-for="(param, pidx) in computedParameters" :key="`parameter${pidx}`">
          <label :for="param.id">{{ param.label || param.id }}</label>
          <input v-if="param.interface === 'text'" type="text" :id="param.id" v-model="values[param.id]" />
          <textarea v-if="param.interface === 'json'" :id="param.id" v-model="values[param.id]" />
          <select v-if="param.interface === 'select'" v-model="values[param.id]">
            <option :value="null" selected disabled>Choose</option>
            <option
              v-for="(option, idx) in param.options"
              :key="`${param.id}-${idx}`"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="response" :class="{ open: response.visible }">
        <div class="title">
          <div>Response</div>
          <div>
            <button @click="responseToClipboard">Copy</button>
            <button @click="toggleResponseVisibility">{{ response.visible ? 'Close' : 'Open' }}</button>
          </div>
        </div>
        <pre class="value" contenteditable="true">{{ response.value }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
  import Button from '@/components/ui/inputs/Button';
  import Toast from '@/components/ui/Toast';
  import UserRoles from '@/objects/UserRoles';
  import Papa from 'papaparse';
  import { Store } from '@/store';

  export default {
    name: 'Debug',
    components: { Button },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {},
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        activeRequest: null,
        globals: { hash: null },
        values: null,

        response: {
          visible: false,
          value: null,
        },

        availableRequests: [
          {
            label: 'Activation',
            requests: [
              {
                label: 'Activate',
                method: 'get',
                url: '/api/debug/on/{code}',
                callback: this.handleActivationResponse,
                parameters: [{ type: 'variable', interface: 'text', id: 'code', label: 'Code' }],
              },
              {
                label: 'Deactivate',
                method: 'get',
                url: '/api/debug/off',
                callback: this.handleActivationResponse,
              },
              {
                label: 'Set Hash',
                method: 'execute',
                callback: this.setHash,
                parameters: [{ type: 'variable', interface: 'text', id: 'hash', label: 'Debug Hash' }],
              },
              {
                label: 'Verify',
                method: 'get',
                url: '/api/debug/verify',
                headers: { 'Auth-Ditoy': '{hash}' },
              },
            ],
          },
          {
            label: 'Inquiry',
            requests: [
              {
                label: 'Read <sup>SAV-ID</sup>',
                method: 'get',
                url: '/api/debug/inquiry/sid/{sid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'sid', label: 'SAV-ID' }],
              },
              {
                label: 'Read <sup>UUID</sup>',
                method: 'get',
                url: '/api/debug/inquiry/uuid/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                label: 'Store',
                method: 'post',
                url: '/api/debug/inquiry',
                headers: { 'Auth-Ditoy': '{hash}', 'Content-Type': 'application/json' },
                parameters: [{ type: 'json-body', interface: 'json', id: 'body', label: 'Body' }],
              },
              {
                label: 'Update Status',
                method: 'post',
                url: '/api/debug/inquiry/status/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}', 'Content-Type': 'application/json' },
                parameters: [
                  { type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' },
                  {
                    type: 'json-object',
                    interface: 'select',
                    id: 'status',
                    label: 'Status',
                    options: [
                      { value: 'open', label: 'Offen' },
                      { value: 'progress', label: 'In Bearbeitung' },
                      { value: 'reviewing', label: 'In Überprüfung' },
                      { value: 'closed', label: 'Abgeschlossen' },
                      { value: 'anonymizing', label: 'In Anonymisierung' },
                      { value: 'anonymized', label: 'Anonymisiert' },
                      { value: 'archived', label: 'Archiviert' },
                    ],
                  },
                ],
              },
              {
                label: 'Delete <sup>UUID</sup>',
                method: 'delete',
                url: '/api/debug/inquiry/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                label: 'VSA Export',
                method: 'get',
                url: '/api/debug/inquiry/vsa/export',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [],
                callback: this.handleVSAExport,
              },
              {
                label: 'VSA Schuljahre <sup>21/22–25/26</sup>',
                method: 'get',
                url: '/api/debug/inquiry/vsa/schoolyears',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [],
              },
            ],
          },
          {
            label: 'User',
            requests: [
              {
                method: 'get',
                label: 'Read <sup>Email</sup>',
                url: '/api/debug/user/email/{email}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'email', label: 'E-Mail' }],
              },
              {
                method: 'get',
                label: 'Read <sup>UUID</sup>',
                url: '/api/debug/user/uuid/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                method: 'post',
                label: 'Store',
                url: '/api/debug/user',
                headers: { 'Auth-Ditoy': '{hash}', 'Content-Type': 'application/json' },
                parameters: [{ type: 'json-body', interface: 'json', id: 'body', label: 'Body' }],
              },
              {
                method: 'get',
                label: 'Read All <sup>UUID</sup>',
                url: '/api/debug/user/all/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                method: 'delete',
                label: 'Delete <sup>UUID</sup>',
                url: '/api/debug/user/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
            ],
          },
          {
            label: 'Branch',
            requests: [
              {
                method: 'get',
                label: 'Read Branch <sup>UUID</sup>',
                url: '/api/debug/branch/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                method: 'post',
                label: 'Store Branch',
                url: '/api/debug/branch',
                headers: { 'Auth-Ditoy': '{hash}', 'Content-Type': 'application/json' },
                parameters: [{ type: 'json-body', interface: 'json', id: 'body', label: 'Body' }],
              },
              {
                method: 'get',
                label: 'Read All <sup>UUID</sup>',
                url: '/api/debug/branch/all/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
              {
                method: 'post',
                label: 'Store All',
                url: '/api/debug/branch/all',
                headers: { 'Auth-Ditoy': '{hash}', 'Content-Type': 'application/json' },
                parameters: [{ type: 'json-body', interface: 'json', id: 'body', label: 'Body' }],
              },
              {
                method: 'delete',
                label: 'Delete All <sup>UUID</sup>',
                url: '/api/debug/branch/all/{uuid}',
                headers: { 'Auth-Ditoy': '{hash}' },
                parameters: [{ type: 'variable', interface: 'text', id: 'uuid', label: 'UUID' }],
              },
            ],
          },
        ],
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedParameters() {
        const request = this.activeRequest;
        return request?.parameters || [];
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    filters: {
      capitalize(value) {
        try {
          return value[0].toUpperCase() + value.slice(1);
        } catch (err) {
          // ignore
        }
        return value;
      },
      callbackName(val) {
        if (val?.name) {
          const splitted = val.name.split(' ');
          return `${splitted[1] || splitted[0]}()`;
        }
        return null;
      },
    },
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    created() {
      if (Store.role !== UserRoles.VSA_MANAGER) {
        this.$router.push('/');
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      const hash = Store.hash;
      if (hash) {
        this.globals.hash = hash;
      }
      window.addEventListener('keypress', this.handleShortcuts);
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      window.removeEventListener('keypress', this.handleShortcuts);
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Request Callback Methods
      //----------------------------------
      handleActivationResponse() {
        this.globals.hash = null;
        Store.hash = null;
        return null;
      },
      setHash() {
        const { hash } = this.values;
        Store.hash = hash || null;
        this.globals.hash = Store.hash;
        return 'Debug Hash has been set';
      },
      async handleVSAExport(response) {
        const data = await response.json();
        data.forEach((o) => {
          const keys = Object.keys(o);
          keys.forEach((key) => {
            const value = o[key];
            // eslint-disable-next-line no-param-reassign
            o[key] = o[key];
            if (Array.isArray(value)) {
              // eslint-disable-next-line no-param-reassign
              o[key] = value.join('|');
            }
          });
        });
        return Papa.unparse(data, { delimiter: ';', header: true });
      },

      //----------------------------------
      // UI Methods
      //----------------------------------
      showRequest(request) {
        this.values = {};
        this.activeRequest = request;
        this.response.value = null;
        this.response.visible = false;
      },
      showResponse(value) {
        this.response.value = value;
        this.response.visible = true;
      },
      toggleResponseVisibility() {
        this.response.visible = !this.response.visible;
      },
      async responseToClipboard() {
        await navigator.clipboard.writeText(this.response.value);
        Toast.success('<strong>Success</strong><br/>Copied to clipboard', 2000);
      },
      handleShortcuts(evt) {
        const ctrl = evt.ctrlKey;
        const key = evt.key;
        if (ctrl && key === 'Enter') {
          this.executeRequest();
        }
      },

      //----------------------------------
      // Request Handling Methods
      //----------------------------------
      async executeRequest() {
        this.response.value = 'Awaiting response ... ';
        const validMethods = ['get', 'post', 'patch', 'delete'];
        const { method, callback } = this.activeRequest;
        if (validMethods.includes(method)) {
          const headers = this.parseHeaders();
          const url = this.parseUrl();
          const body = this.parseBody();

          try {
            const response = await window.fetch(url, {
              method,
              headers,
              credentials: 'include',
              mode: 'same-origin',
              body,
            });

            if (response.ok) {
              if (callback) {
                const callbackResponse = await callback(response);
                if (callbackResponse) {
                  this.showResponse(callbackResponse);
                  return;
                }
              }
              if ((response.headers.get('content-type') || '').includes('json')) {
                this.showResponse(JSON.stringify(await response.json(), null, 2));
              } else {
                this.showResponse(await response.text());
              }
            } else {
              let message;
              if ((response.headers.get('content-type') || '').includes('json')) {
                const json = await response.json();
                const keys = Object.keys(json);
                if (keys.length === 1) {
                  message = json[keys[0]];
                } else {
                  message = JSON.stringify(await response.json(), null, 2);
                }
              } else {
                message = response.text();
              }
              Toast.error(`<strong>${response.statusText} (${response.status})</strong><br/>${message}`);
            }
          } catch (err) {
            Toast.error(err.message);
          }
        } else if (method === 'execute' && callback) {
          const callbackResponse = callback();
          if (callbackResponse) {
            this.showResponse(callbackResponse);
          }
        }
      },
      parseHeaders() {
        let { headers } = this.activeRequest;
        if (headers) {
          headers = JSON.parse(JSON.stringify(headers));
        }
        const headerKeys = Object.keys(headers || {});
        headerKeys.forEach((key) => {
          headers[key] = this.replaceVariables(headers[key]);
        });
        return new Headers(headers);
      },
      parseUrl() {
        let { url } = this.activeRequest;
        url = this.replaceVariables(url);

        const parameters = this.parseURLParameters();
        if (parameters) {
          return new URL(`${url}?${parameters}`, `${window.location.protocol}//${window.location.host}`);
        }
        return new URL(url, `${window.location.protocol}//${window.location.host}`);
      },
      parseURLParameters() {
        // const values = this.values;
        const requestParameters = this.activeRequest?.parameters;
        if (requestParameters) {
          const parsedParams = {};
          requestParameters.forEach((param) => {
            if (param.type === 'query') {
              parsedParams[param.id] = this.values[param.id];
            }
          });

          if (Object.keys(parsedParams).length > 0) {
            return new URLSearchParams(parsedParams);
          }
        }
        return null;
      },
      parseBody() {
        const requestParameters = this.activeRequest?.parameters;
        if (requestParameters) {
          const jsonBody = requestParameters.filter(p => p.type === 'json-body');
          if (jsonBody.length > 0) {
            const param = jsonBody[0];
            const value = this.values[param.id];
            if (typeof value === 'string') {
              return value;
            }
            return JSON.stringify(value);
          }

          const jsonObjects = requestParameters.filter(p => p.type === 'json-object');
          if (jsonObjects.length > 0) {
            const out = {};
            jsonObjects.forEach((param) => {
              out[param.id] = this.values[param.id];
            });
            return JSON.stringify(out);
          }

          const formDataParameters = requestParameters.filter(p => p.type === 'form-data');
          if (formDataParameters.length > 0) {
            const formData = new FormData();
            formDataParameters.forEach((param) => {
              formData.append(param.id, this.values[param.id]);
            });
            return formData;
          }
        }
        return null;
      },
      replaceVariables(source) {
        const singular = /^{\w+}$/;
        const multiple = /{\w+}/g;

        if (singular.test(source)) {
          const variables = source.match(singular).map(v => v.replace(/{|}/g, ''));
          const variable = variables[0] || null;
          return (this.values[variable] || this.globals[variable]) || null;
        }

        if (multiple.test(source)) {
          const variables = source.match(multiple).map(v => v.replace(/{|}/g, ''));
          let value = `${source}`;
          variables.forEach((v) => {
            // eslint-disable-next-line no-param-reassign
            value = value.replace(`{${v}}`, (this.values[v] || this.globals[v]) || null);
          });
          return value;
        }
        return source;
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.debug {
  display: flex;
  min-height: calc(100vh - 80px);

  & > div:first-of-type {
    position: relative;
    background-color: $color-white-solid-10;
    height: inherit;
    min-width: 200px;
    padding: 16px;
    box-shadow: $box-shadow-4;
    z-index: 2;

    details {
      margin: 8px 0;
      &:first-of-type { margin-top: 0; }
      &:last-of-type { margin-bottom: 0; }
      & > summary {
        list-style-type: '+ ';
        @include font-body-small-bold;
        text-transform: uppercase;
        user-select: none;
      }

      &[open] > summary {
        list-style-type: '− ';
      }

      & > ul {
        padding: 4px 0 4px 10px;
        margin: 0;
        list-style-type: none;

        li {
          padding: 0;
          margin: 0 0 4px 0;
          cursor: pointer;
          user-select: none;
          @include font-body-small;

          & > span[data-method] {
            position: relative;
            display: inline-block;
            width: 40px;
            margin-bottom: -3px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            margin-right: 5px;

            &:before {
              text-transform: uppercase;
              content: attr(data-method);
              font-size: 1.1rem;
              font-weight: bold;
            }

            &[data-method=get]:before { color: forestgreen; }
            &[data-method=post]:before { color: orange; }
            &[data-method=patch] { color: tan; }
            &[data-method=delete]:before { color: red; }
          }

          & > span:not([data-method]) {
            & > sup {
              margin-left: 2px;
              display: inline-flex;
              align-items: center;
              background: #dedede;
              padding: 2px 6px;
              border-radius: 10px;
              height: 10px;
              max-height: 10px;
              line-height: 1;
              font-weight: 600;
              font-size: 9px;
              vertical-align: initial;
            }
          }
        }
      }
    }
  }

  & > div:last-of-type {
    position: relative;
    width: 100%;
    z-index: 1;

    & > div.actions {
      display: flex;
      padding: 8px 16px;
      gap: 16px;
      align-items: center;
      justify-content: flex-end;
      background-color: $color-white-solid-10;
      min-height: 48px;
      box-shadow: $box-shadow-2;
      user-select: none;

      & > div.method {
        font-weight: bold;
        text-transform: uppercase;
        &[data-method=get] { color: forestgreen; }
        &[data-method=post] { color: orange; }
        &[data-method=patch] { color: tan; }
        &[data-method=delete] { color: red; }
      }

      & > div.url {
        min-height: 32px;
        border: 1px solid $color-black-alpha-05;
        background-color: $color-black-alpha-05;
        border-radius: $default-border-radius;
        padding: 4px 12px;
        width: 100%;
      }
    }

    & > div.parameters {
      padding: 16px;

      & > .parameter {
        display: flex;
        gap: 16px;
        align-items: center;
        padding: 16px 0;
        border-bottom: 1px solid $color-black-alpha-05;

        &:first-of-type { padding-top: 0; }
        &:last-of-type { border-bottom: none; }

        & > * { width: 100%; }
        & > label {
          @include font-body-small-bold;
          width: 150px;
          user-select: none;
        }
        & > input, textarea, select {
          border-radius: $default-border-radius;
          padding: 4px 11px;
          height: 100%;
          border: 1px solid $color-black-alpha-40;
          outline: 0;
          @include font-body-medium;
          color: $color-black-solid-100;
          user-select: none;
        }

        & > textarea {
          resize: vertical;
          min-height: 200px;
          font-family: monospace;
          font-size: 12px;
        }
      }
    }

    & > div.response {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      background-color: $color-blue-alpha-20;
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);

      & > div.title {
        padding: 0 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 28px;
        width: 100%;
        @include font-body-small-bold;
        color: $color-white-solid-10;
        text-transform: uppercase;
        border-top: 1px solid $color-blue-solid-80;
        background-color: $color-blue-solid-100;
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.4);
        user-select: none;

        & button {
          cursor: pointer;
          margin-left: 4px;
          @include font-body-small;
          color: $color-white-solid-10;
          background-color: $color-white-alpha-10;
          border-radius: $default-border-radius;
          border: 0;
          outline: 0;
          &:hover {
            background-color: $color-white-alpha-20;
          }
        }
      }

      & > pre.value {
        padding: 0;
        height: 0;
        margin: 0;
        font-family: monospace;
        font-size: 12px;
        overflow: hidden;
        overflow-y: scroll;
        user-select: contain;
        outline: 0;
        color: #000;
      }

      &.open {
        height: calc(50% + 28px);

        & > pre.value {
          padding: 8px;
          height: calc(100% - 28px);
        }
      }
    }
  }
}
</style>
